// websocketContext.js
// Escuta única do canal de tempo real, viva no shell da aplicação.
//
// Antes, cada tela abria e fechava a própria conexão (useWebSocket com cleanup
// no unmount), o que reiniciava a escuta a cada troca de rota. O parâmetro
// `key` daquele hook nunca chegou ao servidor: servia apenas como dependência
// de efeito para forçar reconexão. O canal sempre transmitiu todos os ativos,
// então uma conexão única entrega às telas exatamente o mesmo fluxo.
//
// Duas formas de consumo, de propósito:
//
//   `messages`  — estado React com o ÚLTIMO frame. Frames que chegam no mesmo
//                 tick são coalescidos: só o último sobrevive. É o contrato
//                 histórico das telas, que remontam o retrato por ToSenseID e
//                 toleram perder frames intermediários.
//   `subscribe` — entrega SÍNCRONA e SEM PERDA de todo frame recebido. Quem
//                 depende de transição (a notificação de alarme crítico) tem
//                 de usar esta: um frame engolido é um alarme não disparado.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useToken } from "./tokenContext";

const webSocketUrl = process.env.REACT_APP_WEBSOCKET_URL;

// Reconexão com recuo exponencial: uma escuta permanente que morre em silêncio
// deixaria de notificar alarme crítico sem que o operador percebesse.
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30000;

const WebSocketContext = createContext({
  messages: [],
  connected: null,
  subscribe: () => () => {},
});

export const useWebSocketChannel = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const { token } = useToken();
  const [messages, setMessages] = useState([]);
  // Nasce `null` = ainda não se sabe. Nascer `false` faria o aviso de canal
  // caído piscar em toda abertura de sessão, treinando o operador a ignorá-lo
  // justamente no indicador que precisa significar "canal morto".
  const [connected, setConnected] = useState(null);
  const socketRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const subscribersRef = useRef(new Set());

  const subscribe = useCallback((handler) => {
    subscribersRef.current.add(handler);
    return () => {
      subscribersRef.current.delete(handler);
    };
  }, []);

  useEffect(() => {
    // Sem URL configurada o canal não existe — isso é "morto", não "conectando".
    // Deixá-lo em `null` esconderia do operador uma variável de ambiente errada,
    // que é o modo mais provável de canal morto em campo.
    if (!webSocketUrl) {
      setConnected(false);
      return undefined;
    }
    if (!token) return undefined;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      const socket = new WebSocket(webSocketUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        setConnected(true);
        // O token só trafega no corpo da mensagem de abertura, nunca em URL,
        // log ou armazenamento próprio desta camada.
        socket.send(JSON.stringify({ auth: token }));
      };

      socket.onmessage = (event) => {
        let frame;
        try {
          frame = JSON.parse(event.data);
        } catch {
          // Um frame malformado não pode derrubar a escuta de alarme.
          return;
        }

        // O recuo só zera quando a conexão prova ser útil. Zerar no `onopen`
        // faria um servidor que aceita o handshake e derruba em seguida
        // (token expirado, por exemplo) girar em laço fixo de 1 s para sempre.
        //
        // "Útil" precisa ser um frame DO CANAL, não qualquer JSON: a recusa de
        // autenticação costuma vir como `{"error":"unauthorized"}` seguida de
        // fechamento, e aceitar isso como prova de utilidade reintroduziria o
        // mesmo laço de 1 Hz.
        if (frame && frame.ToSenseID !== undefined) retryRef.current = 0;

        // Entrega síncrona: acontece antes de qualquer commit do React, então
        // nenhum assinante perde frame de uma rajada.
        subscribersRef.current.forEach((handler) => {
          try {
            handler(frame);
          } catch {
            // Um assinante que estoura não pode calar os demais.
          }
        });

        setMessages([frame]);
      };

      socket.onclose = () => {
        setConnected(false);
        if (cancelled) return;
        const delay = Math.min(
          RETRY_MAX_MS,
          RETRY_BASE_MS * 2 ** retryRef.current,
        );
        retryRef.current += 1;
        timerRef.current = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // O navegador dispara onclose logo em seguida; a reconexão é tratada lá.
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        socket.onclose = null;
        socket.onmessage = null;
        socket.close();
      }
    };
  }, [token]);

  const value = useMemo(
    () => ({ messages, connected, subscribe }),
    [messages, connected, subscribe],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

WebSocketProvider.propTypes = {
  children: PropTypes.node,
};
